import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Info, X, Pencil, Save } from "lucide-react";
import { RtdOptOut, RtdOptOutBanner } from "@/components/rtd-opt-out";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Sparkles,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Lock,
  BarChart3,
  Wrench,
  TrendingUp,
  Eye,
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Zap,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Loader2,
  FileText,
  PenLine,
  Send,
  Copy,
  Check,
} from "lucide-react";
import { useLocationStore } from "@/lib/store";
import type { Location, AssistantTask, GbpOptimizationJob } from "@shared/schema";
import { useState, useEffect, useRef } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";

interface CategoryRecommendation {
  currentCategory: string | null;
  recommendedCategory: string | null;
  scanAnalysis: Array<{
    keyword: string;
    avgRank: number;
    visibilityScore: number;
    topCompetitorCategories: Array<{ category: string; count: number; avgRank: number }>;
  }>;
  reasoning: string;
}

interface ProfileCompleteness {
  score: number;
  totalFields: number;
  completedFields: number;
  fields: any[];
  mediaCount?: number;
  primaryCategory?: { displayName: string; categoryId?: string };
  additionalCategories?: Array<{ displayName: string; categoryId?: string }>;
  serviceItems?: Array<{ structuredServiceItem?: { serviceTypeId?: string; description?: string }; freeFormServiceItem?: { label?: string; category?: string } }>;
}


// --- inline diff helpers (no external dep) ---
type DiffOp = { type: "same" | "add" | "del"; ch: string };
function diffChars(a: string, b: string): DiffOp[] {
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  const out: DiffOp[] = [];
  let i = m, j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) { out.unshift({ type: "same", ch: a[i - 1] }); i--; j--; }
    else if (dp[i - 1][j] >= dp[i][j - 1]) { out.unshift({ type: "del", ch: a[i - 1] }); i--; }
    else { out.unshift({ type: "add", ch: b[j - 1] }); j--; }
  }
  while (i > 0) { out.unshift({ type: "del", ch: a[i - 1] }); i--; }
  while (j > 0) { out.unshift({ type: "add", ch: b[j - 1] }); j--; }
  return out;
}
function stringSimilarity(a: string, b: string): number {
  if (!a || !b) return 0;
  const la = a.length, lb = b.length;
  if (la === 0 || lb === 0) return 0;
  const dp: number[][] = Array.from({ length: la + 1 }, () => new Array(lb + 1).fill(0));
  for (let i = 1; i <= la; i++) {
    for (let j = 1; j <= lb; j++) {
      dp[i][j] = a[i - 1].toLowerCase() === b[j - 1].toLowerCase()
        ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return dp[la][lb] / Math.max(la, lb);
}
function DiffText({ from, to, side }: { from: string; to: string; side: "from" | "to" }) {
  const ops = diffChars(from, to);
  return (
    <span className="font-mono text-sm">
      {ops.map((op, i) => {
        if (op.type === "same") return <span key={i}>{op.ch}</span>;
        if (side === "from" && op.type === "del") {
          return <span key={i} className="bg-red-500/20 text-red-300 line-through decoration-red-400">{op.ch}</span>;
        }
        if (side === "to" && op.type === "add") {
          return <span key={i} className="bg-emerald-500/20 text-emerald-300 underline decoration-emerald-400">{op.ch}</span>;
        }
        return null;
      })}
    </span>
  );
}

function Step1CategoryCard({
  task,
  isActive,
  isCompleted,
  isLocked,
  locationId,
  profileData,
  location,
  onApprove,
  onDiscard,
}: {
  task: AssistantTask;
  isActive: boolean;
  isCompleted: boolean;
  isLocked: boolean;
  locationId: string;
  profileData?: ProfileCompleteness;
  location?: Location;
  onApprove: () => void;
  onDiscard: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [gbpOpened, setGbpOpened] = useState(false);
  const [recheckLoading, setRecheckLoading] = useState(false);
  const [recheckMessage, setRecheckMessage] = useState<string | null>(null);
  const autoCompletedRef = useRef(false);
  // Step 1's main content is always visible; nothing to auto-expand here.

  const { data: catData, isLoading: catLoading, isError: catError } = useQuery<CategoryRecommendation>({
    queryKey: ["/api/gbp/category-recommendation", locationId],
    enabled: !!locationId && !isLocked,
  });

  if (isLocked) {
    return (
      <Card className="border-[#1E1E2E] bg-[#12121A] relative overflow-hidden" data-testid="card-step1-locked">
        <div className="absolute inset-0 backdrop-blur-[6px] bg-[#12121A]/60 z-10 flex items-center justify-center">
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E1E2E]/90 border border-[#2A2A3A]">
            <Lock className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground font-medium">Complete previous steps to unlock</span>
          </div>
        </div>
        <CardContent className="p-5 select-none opacity-40">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#1E1E2E] border border-[#2A2A3A] flex items-center justify-center text-sm font-bold text-muted-foreground">1</div>
            <h3 className="font-semibold text-muted-foreground">Step 1 · Fix your business category</h3>
          </div>
        </CardContent>
      </Card>
    );
  }

  const currentCat = catData?.currentCategory || "";
  const recCat = catData?.recommendedCategory || "";
  // Normalize for comparison only — the two strings come from different sources
  // (canonical Google taxonomy vs. scraped competitor data) so casing,
  // punctuation, and whitespace can vary even when they're the same category.
  const normalizeCat = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const categoryMatch = !!currentCat && !!recCat && normalizeCat(currentCat) === normalizeCat(recCat);
  const similarity = stringSimilarity(currentCat, recCat);
  const useDiff = similarity > 0.7 && !categoryMatch;

  const recCatNorm = recCat ? normalizeCat(recCat) : "";
  const secondariesToRemove = (profileData?.additionalCategories || []).filter(cat => {
    if (!recCatNorm) return true;
    return normalizeCat(cat.displayName) !== recCatNorm;
  });
  const secondariesRemoved = secondariesToRemove.length === 0;
  const readyToComplete = !!categoryMatch && secondariesRemoved;

  // Auto-complete once both conditions are satisfied (after a successful recheck).
  // Use a ref so rapid re-renders can't double-fire onApprove before state commits.
  useEffect(() => {
    if (!isCompleted && readyToComplete && !autoCompletedRef.current && task.status === "pending") {
      autoCompletedRef.current = true;
      onApprove();
    }
  }, [readyToComplete, isCompleted, task.status, onApprove]);

  // Build the dynamic action checklist.
  const actionItems: string[] = [];
  if (!categoryMatch && recCat) {
    actionItems.push(`Change primary category to "${recCat}"`);
  }
  for (const s of secondariesToRemove) {
    actionItems.push(`Remove "${s.displayName}"`);
  }

  // Aggregate competitor categories across all scans for the evidence chart.
  const aggregatedCategories = (() => {
    if (!catData?.scanAnalysis) return [];
    const map = new Map<string, { appearances: number; rankSum: number }>();
    for (const scan of catData.scanAnalysis) {
      for (const c of scan.topCompetitorCategories) {
        const key = c.category;
        const cur = map.get(key) || { appearances: 0, rankSum: 0 };
        cur.appearances += c.count;
        cur.rankSum += c.avgRank * c.count;
        map.set(key, cur);
      }
    }
    const arr = Array.from(map.entries()).map(([name, v]) => ({
      name,
      appearances: v.appearances,
      avgRank: v.rankSum / Math.max(1, v.appearances),
      isRecommended: !!recCat && name.toLowerCase() === recCat.toLowerCase(),
    }));
    arr.sort((a, b) => b.appearances - a.appearances);
    return arr;
  })();
  const totalAppearances = aggregatedCategories.reduce((s, c) => s + c.appearances, 0);
  const topCategories = aggregatedCategories.slice(0, 5);
  const otherCount = aggregatedCategories.slice(5).reduce((s, c) => s + c.appearances, 0);

  const gbpHref = (() => {
    // Open the Google Business Profile Manager directly. We intentionally avoid
    // account-group deep links (https://business.google.com/n/{account}/profile?fid={loc}):
    // that /n/{account} path is the agency / organization structure and breaks
    // for solo owners whose profile lives on a personal Google account, and the
    // query-param deep link frequently loops back to login on mobile browsers
    // (the standalone GBP app no longer exists). The plain /dashboard landing
    // works universally — the signed-in owner picks their listing there — so it
    // behaves correctly for non-agencies and on mobile alike. This matches the
    // approach used in Profile Essentials and the app header.
    return "https://business.google.com/dashboard";
  })();

  const onRecheck = async () => {
    setRecheckLoading(true);
    setRecheckMessage(null);
    // Refetch so we read fresh data straight from the cache below instead of
    // relying on a stale closure value of `readyToComplete`.
    const [freshCat, freshProfile] = await Promise.all([
      queryClient.fetchQuery<CategoryRecommendation>({ queryKey: ["/api/gbp/category-recommendation", locationId] }),
      queryClient.fetchQuery<ProfileCompleteness>({ queryKey: ["/api/gbp/profile-completeness", locationId] }),
    ]).catch(() => [null, null] as const);
    setRecheckLoading(false);

    const freshCurrent = freshCat?.currentCategory || "";
    const freshRec = freshCat?.recommendedCategory || "";
    const freshMatch = !!freshCurrent && !!freshRec && normalizeCat(freshCurrent) === normalizeCat(freshRec);
    const freshRecNorm = freshRec ? normalizeCat(freshRec) : "";
    const freshSecondariesLeft = (freshProfile?.additionalCategories || []).filter(cat =>
      freshRecNorm ? normalizeCat(cat.displayName) !== freshRecNorm : true
    );

    if (freshMatch && freshSecondariesLeft.length === 0) {
      // Auto-complete useEffect will fire on next render — no message needed.
      return;
    }
    if (!freshRec) {
      setRecheckMessage("We don't have a recommended category yet — your recent GeoGrid scans returned no competitor data. Run a fresh scan from Rank Tracking, then recheck.");
      return;
    }
    if (!freshMatch) {
      setRecheckMessage(`Google still shows your category as "${freshCurrent || "Not set"}". Updates can take a few minutes — try again shortly.`);
      return;
    }
    // Category matches but secondaries still need removing.
    const names = freshSecondariesLeft.map(s => `"${s.displayName}"`).join(", ");
    setRecheckMessage(`Primary category looks good. Still need to remove ${names} from your secondary categories.`);
  };

  const statusPill = isCompleted
    ? <Badge className="text-[10px] bg-emerald-500/20 text-emerald-400">Complete</Badge>
    : recheckLoading
      ? <Badge className="text-[10px] bg-muted text-muted-foreground gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Rechecking…</Badge>
      : gbpOpened
        ? <Badge className="text-[10px] bg-brand-blue/20 text-brand-blue">In Progress</Badge>
        : <Badge className="text-[10px] bg-brand-orange/20 text-brand-orange">Action Required</Badge>;

  return (
    <Card
      className={`border-[#1E1E2E] bg-[#12121A] transition-all ${
        isCompleted ? "border-emerald-500/20" : isActive ? "ring-1 ring-brand-orange/30 border-brand-orange/20" : ""
      }`}
      data-testid="card-step1-category"
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
            isCompleted ? "bg-emerald-500/20" : "bg-brand-orange/20 border border-brand-orange/40"
          }`}>
            {isCompleted ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            ) : (
              <span className="text-sm font-bold text-brand-orange">1</span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            {/* Header row */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <h3 className="text-base font-semibold" data-testid="text-step1-title">
                  Step 1 · Fix your business category
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Match the category top-ranking competitors use
                </p>
              </div>
              {statusPill}
            </div>

            {catLoading ? (
              <div className="mt-4 space-y-2">
                <Skeleton className="h-16" />
                <Skeleton className="h-12" />
              </div>
            ) : catError ? (
              <div className="mt-4 p-3 rounded-lg bg-red-500/5 border border-red-500/20 text-center">
                <p className="text-xs text-red-400">Failed to load category analysis. Please try again later.</p>
              </div>
            ) : !catData ? (
              <div className="mt-4 p-3 rounded-lg bg-[#0A0A0F] border border-[#1E1E2E] text-center">
                <p className="text-sm text-muted-foreground">No GeoGrid scan data available. Run a scan first.</p>
              </div>
            ) : (
              <>
                {/* Comparison block — stacked, not side-by-side */}
                <div className="mt-4 rounded-lg border border-[#1E1E2E] bg-[#0A0A0F] divide-y divide-[#1E1E2E]">
                  <div className="flex items-start gap-3 p-3">
                    <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Current</p>
                      <p className="text-sm font-medium mt-0.5 break-words" data-testid="text-current-category">
                        {currentCat
                          ? (useDiff ? <DiffText from={currentCat} to={recCat} side="from" /> : <span className="font-mono">{currentCat}</span>)
                          : <span className="text-red-400">Not set</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Recommended</p>
                      <p className="text-sm font-medium mt-0.5 break-words" data-testid="text-recommended-category">
                        {recCat
                          ? (useDiff ? <DiffText from={currentCat} to={recCat} side="to" /> : <span className="font-mono">{recCat}</span>)
                          : <span className="text-muted-foreground">Analyzing…</span>}
                      </p>
                    </div>
                  </div>
                </div>
                {useDiff && (
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    Google matches categories exactly — capitalization and punctuation matter.
                  </p>
                )}

                {/* Impact line */}
                {task.benefits && (
                  <div className="mt-3 flex items-start gap-2 text-sm" data-testid="text-impact">
                    <TrendingUp className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span className="text-emerald-300">
                      <span className="font-medium">Expected impact:</span>{" "}
                      <span className="text-muted-foreground">{task.benefits}</span>
                    </span>
                  </div>
                )}

                {/* Action checklist */}
                {!isCompleted && actionItems.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground mb-2">
                      {actionItems.length === 1
                        ? "When you open GBP, do this:"
                        : `When you open GBP, you'll do ${actionItems.length} things:`}
                    </p>
                    <ol className="space-y-1.5">
                      {actionItems.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm" data-testid={`action-item-${i}`}>
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-orange/15 text-brand-orange text-[11px] font-bold shrink-0 mt-0.5">
                            {i + 1}
                          </span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* CTAs */}
                {!isCompleted && (
                  <div className="mt-4 flex items-center gap-3 flex-wrap">
                    <a
                      href={gbpHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setGbpOpened(true)}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium bg-brand-orange text-white hover:bg-brand-orange/90 transition-colors ${isActive && !gbpOpened ? "glow-pulse" : ""}`}
                      data-testid="link-gbp-management"
                    >
                      Open GBP Management
                      <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                    {catData.scanAnalysis.length > 0 && (
                      <button
                        onClick={() => setExpanded(!expanded)}
                        className="text-sm text-muted-foreground hover:text-white flex items-center gap-1"
                        data-testid="button-expand-step1"
                      >
                        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        {expanded ? "Hide evidence" : "Show evidence"}
                      </button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          className="text-sm text-muted-foreground hover:text-white ml-auto"
                          data-testid="button-skip-category"
                        >
                          Not interested in changing my category
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Skip the category change?</AlertDialogTitle>
                          <AlertDialogDescription>
                            <span className="block">
                              We'll mark Step 1 complete and unlock the description and services
                              optimizations so you can keep going.
                            </span>
                            <span className="block mt-2">
                              Heads up: primary category is one of the strongest local-ranking
                              signals on your Google Business Profile. You can always come back
                              and apply this change later — the recommendation will still be here.
                            </span>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="button-skip-category-cancel">
                            Keep this step
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={onDiscard}
                            data-testid="button-skip-category-confirm"
                          >
                            Skip and unlock next steps
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}

                {/* Recheck CTA — only after the user has opened GBP */}
                {!isCompleted && gbpOpened && (
                  <div className="mt-4 p-3 rounded-lg bg-brand-blue/5 border border-brand-blue/20 flex items-center gap-3 flex-wrap">
                    <RefreshCw className="w-4 h-4 text-brand-blue shrink-0" />
                    <span className="text-sm flex-1 min-w-0">Made the changes?</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={onRecheck}
                      disabled={recheckLoading}
                      className="h-8 text-xs gap-1.5 border-brand-blue/30 text-brand-blue hover:bg-brand-blue/10"
                      data-testid="button-recheck-category"
                    >
                      {recheckLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      Recheck My Profile
                    </Button>
                  </div>
                )}
                {recheckMessage && !isCompleted && (
                  <p className="mt-2 text-xs text-amber-400" data-testid="text-recheck-message">
                    {recheckMessage}
                  </p>
                )}

                {/* Evidence (collapsed by default) */}
                {expanded && catData.scanAnalysis.length > 0 && (
                  <div className="mt-4 p-4 rounded-lg bg-[#0A0A0F] border border-[#1E1E2E] space-y-4">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <BarChart3 className="w-4 h-4 text-brand-orange" />
                        <p className="text-sm font-medium">Category performance across competitors</p>
                      </div>
                      <div className="space-y-2">
                        {topCategories.map((c, i) => {
                          const pct = totalAppearances > 0 ? (c.appearances / totalAppearances) * 100 : 0;
                          return (
                            <div key={i} className="grid grid-cols-[1fr_auto] gap-3 items-center" data-testid={`category-bar-${i}`}>
                              <div className="min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <span className={`text-xs truncate ${c.isRecommended ? "text-brand-orange font-medium" : "text-muted-foreground"}`}>
                                    {c.name}
                                  </span>
                                  <span className="text-[11px] text-muted-foreground shrink-0">
                                    {c.appearances}× · rank {c.avgRank.toFixed(1)}
                                  </span>
                                </div>
                                <div className="h-2 bg-[#1E1E2E] rounded-full overflow-hidden">
                                  <div
                                    className={`h-full transition-all duration-500 ${c.isRecommended ? "bg-brand-orange" : "bg-muted-foreground/40"}`}
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {otherCount > 0 && (
                          <p className="text-[11px] text-muted-foreground pt-1">
                            + {aggregatedCategories.length - 5} other {aggregatedCategories.length - 5 === 1 ? "category" : "categories"} ({otherCount} appearances)
                          </p>
                        )}
                      </div>
                    </div>

                    {catData.reasoning && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-amber-400" />
                          <p className="text-sm font-medium">Aria's reasoning</p>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">{catData.reasoning}</p>
                      </div>
                    )}

                    <p className="text-[11px] text-muted-foreground pt-2 border-t border-[#1E1E2E]">
                      Analysis of {catData.scanAnalysis.length} {catData.scanAnalysis.length === 1 ? "scan" : "scans"} ·{" "}
                      {totalAppearances} competitor listing{totalAppearances === 1 ? "" : "s"} ·{" "}
                      Keywords: {catData.scanAnalysis.map(s => `"${s.keyword}"`).join(", ")}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface DescriptionRecommendation {
  currentDescription: string;
  recommendedDescription: string;
  highlights: Array<{ label: string; explanation: string }>;
}

function Step2DescriptionCard({
  task,
  isActive,
  isCompleted,
  isLocked,
  locationId,
  onApprove,
}: {
  task: AssistantTask;
  isActive: boolean;
  isCompleted: boolean;
  isLocked: boolean;
  locationId: string;
  onApprove: () => void;
}) {
  // Auto-expand the FIRST time this step becomes active and not completed.
  // Using a one-shot ref instead of a "previous value" ref so we don't miss
  // the trigger when the card mounts with isActive already true (e.g. page
  // loaded with Step 1 already complete from cached data) or when the parent
  // re-mounts the card due to upstream query state changes.
  const [expanded, setExpanded] = useState(false);
  const didAutoExpandRef = useRef(false);
  useEffect(() => {
    if (!didAutoExpandRef.current && isActive && !isCompleted) {
      setExpanded(true);
      didAutoExpandRef.current = true;
    }
  }, [isActive, isCompleted]);
  const [editedDescription, setEditedDescription] = useState<string | null>(null);
  const { toast } = useToast();

  const generateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/gbp/description-recommendation/${locationId}`, {});
      return res.json() as Promise<DescriptionRecommendation>;
    },
    onError: (err: any) => {
      toast({ title: "Failed to generate description", description: err.message, variant: "destructive" });
    },
  });

  const activeDescription = editedDescription ?? generateMutation.data?.recommendedDescription ?? "";

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!activeDescription || activeDescription.trim().length < 50) throw new Error("Description is too short (minimum 50 characters)");
      if (activeDescription.length > 750) throw new Error("Description exceeds 750 character limit");
      const res = await apiRequest("POST", `/api/gbp/update-description/${locationId}`, {
        description: activeDescription,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Description updated on your Google Business Profile!" });
      onApprove();
    },
    onError: (err: any) => {
      const msg = String(err?.message ?? "");
      // apiRequest throws "<status>: <body>" — detect the 401 GOOGLE_NOT_CONNECTED case
      if (msg.startsWith("401:") && msg.includes("GOOGLE_NOT_CONNECTED")) {
        toast({
          title: "Reconnect Google to continue",
          description: "Your Google account isn't connected. Reconnect it from Settings to update your Business Profile.",
          variant: "destructive",
          action: (
            <ToastAction altText="Go to Settings" onClick={() => { window.location.href = "/settings"; }}>
              Go to Settings
            </ToastAction>
          ),
        });
        return;
      }
      toast({ title: "Failed to update description", description: msg, variant: "destructive" });
    },
  });

  const descData = generateMutation.data;

  if (isLocked) {
    return (
      <Card className="border-[#1E1E2E] bg-[#12121A] relative overflow-hidden" data-testid="card-step2-description-locked">
        <div className="absolute inset-0 backdrop-blur-[6px] bg-[#12121A]/60 z-10 flex items-center justify-center">
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E1E2E]/90 border border-[#2A2A3A]">
            <Lock className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground font-medium">Complete Step 1 to unlock</span>
          </div>
        </div>
        <CardContent className="p-5 select-none opacity-40">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#1E1E2E] border border-[#2A2A3A] flex items-center justify-center text-sm font-bold text-muted-foreground">2</div>
            <h3 className="font-semibold text-muted-foreground">Add a Business Profile Description that Establishes Your Authority</h3>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={`border-[#1E1E2E] bg-[#12121A] transition-all ${
        isCompleted ? "border-emerald-500/20" : isActive ? "ring-1 ring-brand-orange/30 border-brand-orange/20" : ""
      }`}
      data-testid="card-step2-description"
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
            isCompleted ? "bg-emerald-500/20" : "bg-brand-orange/20 border border-brand-orange/40"
          }`}>
            {isCompleted ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            ) : (
              <span className="text-sm font-bold text-brand-orange">2</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-semibold">Add a Business Profile Description that Establishes Your Authority</h3>
              {isCompleted && <Badge className="text-[10px] bg-emerald-500/20 text-emerald-400">Completed</Badge>}
              {isActive && !isCompleted && <Badge className="text-[10px] bg-brand-orange/20 text-brand-orange animate-pulse">Action Required</Badge>}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Aria generates an entity-rich, authority-building description optimized for local search
            </p>

            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-brand-orange hover:text-brand-orange/80 mt-3 flex items-center gap-1"
              data-testid="button-expand-step2-description"
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? "Hide details" : "Get started"}
            </button>

            {expanded && !descData && (
              <div className="mt-4 space-y-4">
                <div className="p-3 rounded-lg bg-brand-orange/5 border border-brand-orange/20">
                  <p className="text-xs font-medium text-brand-orange mb-1">AI-Powered Entity Description</p>
                  <p className="text-[11px] text-muted-foreground">Aria will use Gemini AI to research real, verifiable entities (suppliers, partners, associations) for your business and weave them into an authority description optimized for Google's Knowledge Graph.</p>
                </div>

                <div className="pt-2 border-t border-border/30">
                  <div className="flex justify-end">
                    <Button
                      className={`bg-brand-orange hover:bg-brand-orange/90 ${isActive && !generateMutation.isPending ? "glow-pulse" : ""}`}
                      onClick={() => generateMutation.mutate()}
                      disabled={generateMutation.isPending}
                      data-testid="button-generate-description"
                    >
                      {generateMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Aria is researching entities...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate Authority Description
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {expanded && descData && (
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-[#0A0A0F] border border-[#1E1E2E]">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Current Description</p>
                    </div>
                    {descData.currentDescription ? (
                      <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap">{descData.currentDescription}</p>
                    ) : (
                      <div className="flex items-center gap-2 py-3">
                        <AlertCircle className="w-4 h-4 text-red-400" />
                        <p className="text-xs text-red-400">No description set — your profile is missing a critical ranking signal</p>
                      </div>
                    )}
                    {descData.currentDescription && (
                      <p className="text-[10px] text-muted-foreground/60 mt-2">{descData.currentDescription.length} / 750 characters</p>
                    )}
                  </div>

                  <div className="p-3 rounded-lg bg-brand-orange/5 border border-brand-orange/20">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <PenLine className="w-3.5 h-3.5 text-brand-orange" />
                        <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-orange">
                          {editedDescription !== null ? "Your Edited Description" : "Aria's Recommended Description"}
                        </p>
                      </div>
                      {editedDescription !== null && (
                        <button
                          onClick={() => setEditedDescription(null)}
                          className="text-[10px] text-muted-foreground hover:text-brand-orange transition-colors"
                          data-testid="button-reset-description"
                        >
                          Reset to original
                        </button>
                      )}
                    </div>
                    <textarea
                      className="w-full bg-transparent text-xs text-foreground leading-relaxed resize-none border border-[#2A2A3A] rounded-md p-2 focus:outline-none focus:ring-1 focus:ring-brand-orange/40 focus:border-brand-orange/30 min-h-[100px]"
                      style={{ minHeight: "100px" }}
                      value={activeDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      maxLength={750}
                      data-testid="textarea-edit-description"
                    />
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-[10px] text-muted-foreground/40">Click to edit before pushing live</p>
                      <p className={`text-[10px] ${activeDescription.length > 700 ? (activeDescription.length > 750 ? "text-red-400" : "text-yellow-400") : "text-muted-foreground/60"}`}>
                        {activeDescription.length} / 750 characters
                      </p>
                    </div>
                  </div>
                </div>

                {descData.highlights.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" />
                      Why This Description Will Make You More Money
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {descData.highlights.map((h, i) => (
                        <div key={i} className="p-2.5 rounded-lg bg-[#0A0A0F] border border-[#1E1E2E]" data-testid={`highlight-${i}`}>
                          <p className="text-[11px] font-semibold text-emerald-400 flex items-center gap-1.5">
                            <CheckCircle2 className="w-3 h-3" />
                            {h.label}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{h.explanation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/10">
                    <div className="flex items-center gap-1.5 mb-1">
                      <AlertCircle className="w-3 h-3 text-red-400" />
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-red-400">Without This</p>
                    </div>
                    <p className="text-xs text-muted-foreground">{task.shortcomings}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                    <div className="flex items-center gap-1.5 mb-1">
                      <TrendingUp className="w-3 h-3 text-emerald-400" />
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-emerald-400">Expected Impact</p>
                    </div>
                    <p className="text-xs text-muted-foreground">{task.benefits}</p>
                  </div>
                </div>

                {!isCompleted && task.status === "pending" && (
                  <div className="pt-2 border-t border-border/30">
                    <div className="flex justify-between">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setEditedDescription(null); generateMutation.reset(); }}
                        className="text-xs"
                        data-testid="button-regenerate-description"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Regenerate
                      </Button>
                      <Button
                        className={`bg-brand-orange hover:bg-brand-orange/90 ${isActive && !updateMutation.isPending ? "glow-pulse" : ""}`}
                        onClick={() => updateMutation.mutate()}
                        disabled={updateMutation.isPending}
                        data-testid="button-update-description"
                      >
                        {updateMutation.isPending ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Update My Description
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface VerificationComparison {
  keyword: string;
  keywordId: string;
  before: { scanId: string; avgRank: number; visibilityScore: number; status: string; scannedAt: string };
  after: { scanId: string; avgRank: number; visibilityScore: number; status: string; scannedAt: string };
}

function Step4VerificationCard({
  task,
  isActive,
  isCompleted,
  isLocked,
  locationId,
  onApprove,
}: {
  task: AssistantTask;
  isActive: boolean;
  isCompleted: boolean;
  isLocked: boolean;
  locationId: string;
  onApprove: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [scanTriggered, setScanTriggered] = useState(false);
  const { toast } = useToast();

  const { data: comparisonData, isLoading: compLoading, refetch: refetchComparison } = useQuery<{
    comparisons: VerificationComparison[];
    allComplete: boolean;
  }>({
    queryKey: ["/api/gbp/verification-comparison", locationId],
    enabled: !!locationId && !isLocked && expanded,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return scanTriggered ? 5000 : false;
      const hasIncomplete = data.comparisons.some(
        c => c.after.status === "pending" || c.after.status === "processing"
      );
      return hasIncomplete ? 5000 : false;
    },
  });

  const hasAfterScans = comparisonData && comparisonData.comparisons.length > 0;
  const allComplete = comparisonData?.allComplete || false;
  const anyPending = comparisonData?.comparisons.some(c => c.after.status === "pending" || c.after.status === "processing");
  const anyFailed = comparisonData?.comparisons.some(c => c.after.status === "failed");
  const canRetry = !anyPending && !allComplete;

  useEffect(() => {
    if (allComplete && scanTriggered) {
      setScanTriggered(false);
    }
  }, [allComplete, scanTriggered]);

  const triggerMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/gbp/verification-scans/${locationId}`);
      return res.json();
    },
    onSuccess: (data: any) => {
      setScanTriggered(true);
      toast({ title: `${data.triggered} free verification scans started!` });
      queryClient.invalidateQueries({ queryKey: ["/api/gbp/verification-comparison", locationId] });
    },
    onError: (err: any) => {
      toast({ title: "Could not start scans", description: err.message, variant: "destructive" });
    },
  });

  if (isLocked) {
    return (
      <Card className="border-[#1E1E2E] bg-[#12121A] relative overflow-hidden" data-testid="card-step4-verification-locked">
        <div className="absolute inset-0 backdrop-blur-[6px] bg-[#12121A]/60 z-10 flex items-center justify-center">
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E1E2E]/90 border border-[#2A2A3A]">
            <Lock className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground font-medium">Complete Steps 1-3 to unlock</span>
          </div>
        </div>
        <CardContent className="p-5 select-none opacity-40">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#1E1E2E] border border-[#2A2A3A] flex items-center justify-center text-sm font-bold text-muted-foreground">4</div>
            <h3 className="font-semibold text-muted-foreground">Prove Your Visibility Improvement</h3>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={`border-[#1E1E2E] bg-[#12121A] transition-all ${
        isCompleted ? "border-emerald-500/20" : isActive ? "ring-1 ring-brand-orange/30 border-brand-orange/20" : ""
      }`}
      data-testid="card-step4-verification"
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
            isCompleted ? "bg-emerald-500/20" : "bg-brand-orange/20 border border-brand-orange/40"
          }`}>
            {isCompleted ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            ) : (
              <span className="text-sm font-bold text-brand-orange">4</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-semibold">Prove Your Visibility Improvement</h3>
              {isCompleted && <Badge className="text-[10px] bg-emerald-500/20 text-emerald-400">Completed</Badge>}
              {isActive && !isCompleted && <Badge className="text-[10px] bg-brand-orange/20 text-brand-orange animate-pulse">Action Required</Badge>}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Re-run your GeoGrid scans for free to measure the impact of your category optimization
            </p>

            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-brand-orange hover:text-brand-orange/80 mt-3 flex items-center gap-1"
              data-testid="button-expand-step4-verify"
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? "Hide details" : "View verification"}
            </button>

            {expanded && (
              <div className="mt-4 space-y-4">
                <div className="p-3 rounded-lg bg-[#0A0A0F] border border-[#1E1E2E]">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-4 h-4 text-brand-orange" />
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-orange">Why Verify?</p>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    After optimizing your business category, you need proof that it's working. Running verification scans
                    re-checks your rankings across all your original keywords. Increased visibility means more customers
                    are finding your business when searching for your services.
                  </p>
                </div>

                {compLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : (!hasAfterScans && !scanTriggered) || (anyFailed && canRetry) ? (
                  <div className="text-center py-4 space-y-3">
                    <div className="w-12 h-12 rounded-full bg-brand-orange/10 flex items-center justify-center mx-auto">
                      <RefreshCw className="w-6 h-6 text-brand-orange" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{anyFailed ? "Some scans failed — retry?" : "Ready to verify your improvement"}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        This will re-scan all your original keywords at no cost. Scans typically take 5-10 minutes total — you can leave and come back.
                      </p>
                    </div>
                    <Button
                      className="bg-brand-orange hover:bg-brand-orange/90"
                      onClick={() => triggerMutation.mutate()}
                      disabled={triggerMutation.isPending}
                      data-testid="button-run-verification-scans"
                    >
                      {triggerMutation.isPending ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Zap className="w-4 h-4 mr-2" />
                      )}
                      {anyFailed ? "Retry Verification Scans" : "Run Free Verification Scans"}
                    </Button>
                  </div>
                ) : (
                  <>
                    {anyPending && (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-brand-blue/5 border border-brand-blue/20">
                        <Loader2 className="w-4 h-4 text-brand-blue animate-spin shrink-0" />
                        <div>
                          <p className="text-xs text-brand-blue font-medium">Verification scans in progress...</p>
                          <p className="text-[11px] text-brand-blue/70 mt-0.5">Each keyword scan takes 2-3 minutes. You can leave this page and come back — scans run in the background.</p>
                        </div>
                      </div>
                    )}

                    {comparisonData && comparisonData.comparisons.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground flex items-center gap-1.5">
                          <BarChart3 className="w-3.5 h-3.5" />
                          Before vs After Comparison
                        </p>
                        <div className="space-y-2">
                          {comparisonData.comparisons.map((comp, i) => {
                            const visChange = comp.after.visibilityScore - comp.before.visibilityScore;
                            const rankChange = comp.before.avgRank - comp.after.avgRank;
                            const isImproved = visChange > 0;
                            const isDeclined = visChange < 0;
                            const afterPending = comp.after.status !== "completed";

                            return (
                              <div key={i} className="p-3 rounded-lg bg-[#0A0A0F] border border-[#1E1E2E]" data-testid={`comparison-row-${i}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-xs font-medium flex items-center gap-1.5">
                                    <Eye className="w-3 h-3 text-brand-blue" />
                                    "{comp.keyword}"
                                  </p>
                                  {afterPending && (
                                    <Badge className="text-[10px] bg-brand-blue/20 text-brand-blue">
                                      <Loader2 className="w-2.5 h-2.5 mr-1 animate-spin" />
                                      Scanning...
                                    </Badge>
                                  )}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
                                  <div className="text-center p-2 rounded bg-[#1E1E2E]/50">
                                    <p className="text-[9px] uppercase text-muted-foreground mb-0.5">Before</p>
                                    <p className="text-sm font-bold text-muted-foreground">{comp.before.visibilityScore.toFixed(0)}%</p>
                                    <p className="text-[9px] text-muted-foreground">rank {comp.before.avgRank.toFixed(1)}</p>
                                  </div>
                                  <div className="text-center p-2 rounded bg-[#1E1E2E]/50">
                                    <p className="text-[9px] uppercase text-muted-foreground mb-0.5">After</p>
                                    {afterPending ? (
                                      <Loader2 className="w-4 h-4 animate-spin mx-auto text-muted-foreground" />
                                    ) : (
                                      <>
                                        <p className="text-sm font-bold text-white">{comp.after.visibilityScore.toFixed(0)}%</p>
                                        <p className="text-[9px] text-muted-foreground">rank {comp.after.avgRank.toFixed(1)}</p>
                                      </>
                                    )}
                                  </div>
                                  <div className={`text-center p-2 rounded ${
                                    afterPending ? "bg-[#1E1E2E]/50" :
                                    isImproved ? "bg-emerald-500/10" : isDeclined ? "bg-red-500/10" : "bg-[#1E1E2E]/50"
                                  }`}>
                                    <p className="text-[9px] uppercase text-muted-foreground mb-0.5">Change</p>
                                    {afterPending ? (
                                      <Minus className="w-4 h-4 mx-auto text-muted-foreground" />
                                    ) : (
                                      <>
                                        <p className={`text-sm font-bold flex items-center justify-center gap-0.5 ${
                                          isImproved ? "text-emerald-400" : isDeclined ? "text-red-400" : "text-muted-foreground"
                                        }`}>
                                          {isImproved ? <ArrowUpRight className="w-3.5 h-3.5" /> : isDeclined ? <ArrowDownRight className="w-3.5 h-3.5" /> : null}
                                          {visChange > 0 ? "+" : ""}{visChange.toFixed(1)}%
                                        </p>
                                        <p className={`text-[9px] ${isImproved ? "text-emerald-400/70" : isDeclined ? "text-red-400/70" : "text-muted-foreground"}`}>
                                          {rankChange > 0 ? `↑${rankChange.toFixed(1)}` : rankChange < 0 ? `↓${Math.abs(rankChange).toFixed(1)}` : "—"} rank
                                        </p>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {allComplete && (
                          <div className={`p-3 rounded-lg border ${
                            comparisonData.comparisons.some(c => c.after.visibilityScore - c.before.visibilityScore > 0)
                              ? "bg-emerald-500/5 border-emerald-500/20"
                              : "bg-[#0A0A0F] border-[#1E1E2E]"
                          }`}>
                            <div className="flex items-center gap-2 mb-1">
                              <TrendingUp className="w-4 h-4 text-emerald-400" />
                              <p className="text-[10px] uppercase tracking-wider font-semibold text-emerald-400">Analysis Complete</p>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {(() => {
                                const improved = comparisonData.comparisons.filter(c => c.after.visibilityScore > c.before.visibilityScore).length;
                                const total = comparisonData.comparisons.length;
                                const avgVisChange = comparisonData.comparisons.reduce((sum, c) => sum + (c.after.visibilityScore - c.before.visibilityScore), 0) / total;
                                if (improved === total) return `All ${total} keywords show improved visibility (avg +${avgVisChange.toFixed(1)}%). Your category optimization is driving more customer reach across all search terms.`;
                                if (improved > 0) return `${improved} of ${total} keywords show improved visibility. Your optimization is working — increased visibility means more customers finding your business when they search for your services.`;
                                return `Visibility scores are consistent. Your category was already well-optimized. The verification confirms your strong positioning.`;
                              })()}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/10">
                    <div className="flex items-center gap-1.5 mb-1">
                      <AlertCircle className="w-3 h-3 text-red-400" />
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-red-400">Why This Matters</p>
                    </div>
                    <p className="text-xs text-muted-foreground">{task.shortcomings}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                    <div className="flex items-center gap-1.5 mb-1">
                      <TrendingUp className="w-3 h-3 text-emerald-400" />
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-emerald-400">Expected Impact</p>
                    </div>
                    <p className="text-xs text-muted-foreground">{task.benefits}</p>
                  </div>
                </div>

                {!isCompleted && task.status === "pending" && (
                  <div className="space-y-2 pt-2 border-t border-border/30">
                    {!allComplete && (
                      <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/15">
                        <AlertCircle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                        <p className="text-[11px] text-amber-400">
                          Run the free verification scans and wait for all results to complete this step.
                        </p>
                      </div>
                    )}
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        className={allComplete ? "bg-brand-orange hover:bg-brand-orange/90" : "bg-muted text-muted-foreground cursor-not-allowed"}
                        onClick={allComplete ? onApprove : undefined}
                        disabled={!allComplete}
                        data-testid="button-approve-step4-verify"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                        Complete Step
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface ServiceDescResult {
  landmarks: Array<{ name: string; crossStreet: string; city: string; zone?: string; gridRank?: number }>;
  services: Array<{
    name: string;
    upgradedName: string;
    currentDescription: string;
    optimizedDescription: string;
    assignedLandmark: string;
    landmarkZone: string;
    matchedKeyword: string;
    openerPattern: number;
    charCount: number;
    isProtected: boolean;
    googleServiceTypeId?: string;
    validationFlags: string[];
  }>;
  stats: {
    totalServices: number;
    protectedCount: number;
    freeformCount: number;
    passCount: number;
    flagCount: number;
  };
  audit: {
    totalPassed: number;
    totalFlagged: number;
    issues: string[];
  };
  progress: string[];
}

type KeywordVariants = {
  directVariations: string[];
  serviceLongTail: string[];
  problemQuestions: string[];
  buyerStageModifiers: string[];
};

type GeogridPoint = { lat: number; lng: number; rank: number; zone: string };

function classifyZone(rank: number): string {
  if (rank <= 3) return "green";
  if (rank <= 7) return "yellow";
  if (rank <= 13) return "orange";
  return "red";
}

const ZONE_COLORS: Record<string, string> = {
  green: "#3db87a",
  yellow: "#d4a017",
  orange: "#e8722a",
  red: "#e8722a",
};

const ZONE_LABELS: Record<string, { icon: string; label: string }> = {
  green: { icon: "🏆", label: "Top 3 Locked" },
  yellow: { icon: "⚡", label: "Top 10" },
  orange: { icon: "📈", label: "Climbing" },
  red: { icon: "🎯", label: "Expansion Target" },
};

function highlightKeywordsInText(text: string, trackedKeywords: string[]) {
  if (!trackedKeywords.length) return <>{text}</>;
  const escaped = trackedKeywords.map(kw => kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const pattern = new RegExp(`(${escaped.join("|")})`, "gi");
  const parts = text.split(pattern);
  return (
    <>
      {parts.map((part, i) => {
        const isMatch = trackedKeywords.some(kw => kw.toLowerCase() === part.toLowerCase());
        return isMatch ? (
          <span key={i} className="text-amber-400 underline decoration-amber-400/40 underline-offset-2">{part}</span>
        ) : (
          <span key={i}>{part}</span>
        );
      })}
    </>
  );
}

function findMatchedKeyword(text: string, trackedKeywords: string[]): string | null {
  const lower = text.toLowerCase();
  for (const kw of trackedKeywords) {
    if (lower.includes(kw.toLowerCase())) return kw;
  }
  return null;
}

function Step3ServicesCard({
  task,
  isActive,
  isCompleted,
  isLocked,
  locationId,
  profileData,
  location,
  onApprove,
  onDiscard,
}: {
  task: AssistantTask;
  isActive: boolean;
  isCompleted: boolean;
  isLocked: boolean;
  locationId: string;
  profileData?: ProfileCompleteness;
  location?: Location;
  onApprove: () => void;
  onDiscard: () => void;
}) {
  const pushLatchKey = `dd_push_done_${locationId}`;
  const titlesKey = `dd_svc_titles_${locationId}`;
  const uncheckedKey = `dd_svc_unchecked_${locationId}`;
  const [copiedServices, setCopiedServices] = useState(false);
  const [pushStatus, setPushStatus] = useState<{ state: "idle" | "pushing" | "success" | "error"; message?: string; timestamp?: string }>(() => {
    try {
      const saved = localStorage.getItem(pushLatchKey);
      if (saved) return JSON.parse(saved);
    } catch {}
    return { state: "idle" };
  });
  const [showPushConfirm, setShowPushConfirm] = useState(false);
  const [pushCoachDismissed, setPushCoachDismissed] = useState(false);
  const pushBtnRef = useRef<HTMLButtonElement>(null);
  const [coachTop, setCoachTop] = useState<number | null>(null);
  const [uncheckedTitles, setUncheckedTitles] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(uncheckedKey);
      if (saved) return new Set(JSON.parse(saved));
    } catch {}
    return new Set();
  });
  const [generatedForLocationId, setGeneratedForLocationId] = useState<string | null>(null);
  const [progressSteps, setProgressSteps] = useState<string[]>([]);
  const [progressAnimating, setProgressAnimating] = useState(false);
  const [genStartTime, setGenStartTime] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [cachedDescData, setCachedDescData] = useState<ServiceDescResult | null>(null);
  // Per-service curation state
  const excludedKey = `dd_svc_excluded_${locationId}`;
  const editsKey = `dd_svc_edits_${locationId}`;
  const [excludedIdx, setExcludedIdx] = useState<Set<number>>(() => {
    try { const s = localStorage.getItem(`dd_svc_excluded_${locationId}`); if (s) return new Set(JSON.parse(s)); } catch {}
    return new Set();
  });
  const [editDrafts, setEditDrafts] = useState<Record<number, { name: string; description: string }>>(() => {
    try { const s = localStorage.getItem(`dd_svc_edits_${locationId}`); if (s) return JSON.parse(s); } catch {}
    return {};
  });
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [filter, setFilter] = useState<"all" | "expansion" | "edited" | "excluded">("all");
  const { toast } = useToast();
  const credential = "";

  const { data: inputsData } = useQuery<{ trackedKeywords: string[]; geogridZones: GeogridPoint[] }>({
    queryKey: ["/api/gbp/service-desc-inputs", locationId],
    enabled: !!locationId && !isLocked,
  });

  const trackedKeywords = inputsData?.trackedKeywords || [];
  const geogridZones = inputsData?.geogridZones || [];

  const currentServices = (profileData?.serviceItems || []).map(s => {
    const isStructured = !!s.structuredServiceItem;
    const label = s.freeFormServiceItem?.label;
    const labelObj = label && typeof label === "object" ? label as Record<string, string> : null;
    const labelName = labelObj ? labelObj.displayName : (typeof label === "string" ? label : undefined);
    const labelDesc = labelObj ? labelObj.description : "";
    if (isStructured) {
      const rawId = s.structuredServiceItem!.serviceTypeId || "";
      const displayName = rawId
        .replace(/^.*:/, "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase());
      return {
        name: displayName || "Unknown Service",
        description: s.structuredServiceItem!.description || "",
        isProtected: true,
        googleServiceTypeId: rawId,
      };
    }
    return {
      name: labelName || "Unknown Service",
      description: labelDesc || "",
      isProtected: false,
    };
  });

  const [additionalTitles, setAdditionalTitles] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(titlesKey);
      if (saved) return JSON.parse(saved);
    } catch {}
    return [];
  });

  const MAX_GOOGLE_SERVICES = 100;
  const allCurrentServicesRaw = [
    ...currentServices,
    ...additionalTitles.filter(t => !uncheckedTitles.has(t)).map(t => ({ name: t, description: "", isProtected: false })),
  ];
  const allCurrentServices = allCurrentServicesRaw.slice(0, MAX_GOOGLE_SERVICES);
  const totalSlots = allCurrentServices.length;
  const businessName = location?.name || "";
  const primaryCategory = location?.category || "";

  // Server-side job state: titles + descriptions both run as background jobs.
  // On mount we hydrate from /api/gbp/jobs/latest so navigation/refresh restores progress.
  const [titlesJobId, setTitlesJobId] = useState<string | null>(null);
  const [descJobId, setDescJobId] = useState<string | null>(null);
  const [consumedTitlesJobId, setConsumedTitlesJobId] = useState<string | null>(null);
  const [consumedDescJobId, setConsumedDescJobId] = useState<string | null>(null);
  const [titlesKickedThisLocation, setTitlesKickedThisLocation] = useState(false);

  const { data: latestTitlesJob } = useQuery<GbpOptimizationJob | null>({
    queryKey: ["/api/gbp/jobs/latest", locationId, "service_titles"],
    queryFn: async () => {
      const r = await fetch(`/api/gbp/jobs/latest?locationId=${encodeURIComponent(locationId)}&kind=service_titles`, { credentials: "include" });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    enabled: !!locationId && !isLocked,
  });
  const { data: latestDescJob } = useQuery<GbpOptimizationJob | null>({
    queryKey: ["/api/gbp/jobs/latest", locationId, "service_descriptions"],
    queryFn: async () => {
      const r = await fetch(`/api/gbp/jobs/latest?locationId=${encodeURIComponent(locationId)}&kind=service_descriptions`, { credentials: "include" });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
    enabled: !!locationId && !isLocked,
  });

  // Adopt the latest job (processing → resume polling; completed → restore result once)
  useEffect(() => {
    if (!latestTitlesJob || titlesJobId) return;
    if (latestTitlesJob.status === "processing") {
      setTitlesJobId(latestTitlesJob.id);
    }
  }, [latestTitlesJob, titlesJobId]);
  useEffect(() => {
    if (!latestDescJob || descJobId) return;
    if (latestDescJob.status === "processing") {
      setDescJobId(latestDescJob.id);
      return;
    }
    if (latestDescJob.status === "completed") {
      // Only auto-restore completed descriptions that are recent (24h).
      const createdAt = latestDescJob.createdAt ? new Date(latestDescJob.createdAt as any).getTime() : 0;
      if (Date.now() - createdAt < 24 * 60 * 60 * 1000) {
        setDescJobId(latestDescJob.id);
      }
    }
  }, [latestDescJob, descJobId]);

  const { data: titlesJob } = useQuery<GbpOptimizationJob>({
    queryKey: ["/api/gbp/jobs", titlesJobId],
    enabled: !!titlesJobId,
    refetchInterval: (q) => (q.state.data?.status === "processing" ? 2000 : false),
  });
  const { data: descJob } = useQuery<GbpOptimizationJob>({
    queryKey: ["/api/gbp/jobs", descJobId],
    enabled: !!descJobId,
    refetchInterval: (q) => (q.state.data?.status === "processing" ? 2000 : false),
  });

  // Consume terminal titles job → append titles + toast (once per job id)
  useEffect(() => {
    if (!titlesJob || titlesJob.id === consumedTitlesJobId) return;
    if (titlesJob.status === "completed") {
      const data = titlesJob.result as { titles?: string[] } | null;
      if (data?.titles?.length) {
        setAdditionalTitles(prev => [...prev, ...(data.titles || [])]);
        if (titlesKickedThisLocation) {
          toast({ title: `${data.titles.length} new services generated`, description: `Now at ${totalSlots + data.titles.length}/100 slots` });
        }
      }
      setConsumedTitlesJobId(titlesJob.id);
      setTitlesJobId(null);
    } else if (titlesJob.status === "failed") {
      if (titlesKickedThisLocation) {
        toast({ title: "Service Generation Failed", description: titlesJob.errorMessage || "Could not generate services", variant: "destructive" });
      }
      setConsumedTitlesJobId(titlesJob.id);
      setTitlesJobId(null);
    }
  }, [titlesJob, consumedTitlesJobId, totalSlots, titlesKickedThisLocation, toast]);

  const fillMutation = useMutation<{ jobId: string }>({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/gbp/generate-service-titles/${locationId}`, {
        currentServices: allCurrentServices,
        landmarks: [],
        keywords: trackedKeywords,
        keywordVariants: {},
        businessName,
        primaryCategory,
        credential: credential || undefined,
      });
      return res.json();
    },
    onSuccess: (data) => {
      setTitlesKickedThisLocation(true);
      setConsumedTitlesJobId(null);
      setTitlesJobId(data.jobId);
    },
    onError: (err: any) => {
      toast({ title: "Service Generation Failed", description: err.message, variant: "destructive" });
    },
  });

  const isTitlesRunning = fillMutation.isPending
    || (!!titlesJob && titlesJob.status === "processing")
    || (!!titlesJobId && !titlesJob);
  const titlesDidComplete = consumedTitlesJobId !== null;
  const step1Done = totalSlots >= 100 || (titlesDidComplete && totalSlots >= 20);

  useEffect(() => {
    try { localStorage.setItem(titlesKey, JSON.stringify(additionalTitles)); } catch {}
  }, [additionalTitles, titlesKey]);

  useEffect(() => {
    try { localStorage.setItem(uncheckedKey, JSON.stringify([...uncheckedTitles])); } catch {}
  }, [uncheckedTitles, uncheckedKey]);

  useEffect(() => {
    try { localStorage.setItem(excludedKey, JSON.stringify([...excludedIdx])); } catch {}
  }, [excludedIdx, excludedKey]);

  useEffect(() => {
    try { localStorage.setItem(editsKey, JSON.stringify(editDrafts)); } catch {}
  }, [editDrafts, editsKey]);

  useEffect(() => {
    if (genStartTime === null) return;
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - genStartTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [genStartTime]);

  // Consume terminal desc job → set cached data + animate completion (once per job id)
  useEffect(() => {
    if (!descJob || descJob.id === consumedDescJobId) return;
    if (descJob.status === "completed") {
      const data = descJob.result as ServiceDescResult | null;
      setGenStartTime(null);
      setProgressAnimating(false);
      if (data?.progress?.length) {
        setProgressSteps(data.progress);
      } else if (Array.isArray(descJob.progressSteps)) {
        setProgressSteps(descJob.progressSteps as string[]);
      }
      if (data && !(data.services?.length === 0 && (data.audit?.issues?.length ?? 0) > 0)) {
        setGeneratedForLocationId(locationId);
        setCachedDescData(data);
        setPushCoachDismissed(false);
        try { localStorage.removeItem(`dd_push_coach_dismissed_${locationId}`); } catch {}
        try { localStorage.setItem(`dd_desc_result_${locationId}`, JSON.stringify(data)); } catch {}
      } else if (data) {
        toast({ title: "Optimizing", description: "Preparing additional landmark data. Please try again in a moment.", variant: "default" });
      }
      setConsumedDescJobId(descJob.id);
      setDescJobId(null);
    } else if (descJob.status === "failed") {
      setGenStartTime(null);
      setProgressAnimating(false);
      toast({ title: "Build Failed", description: descJob.errorMessage || "Could not generate descriptions", variant: "destructive" });
      setConsumedDescJobId(descJob.id);
      setDescJobId(null);
    } else if (descJob.status === "processing") {
      // Resuming an in-flight job after navigation: show progress UI without re-animating from 0.
      if (genStartTime === null) {
        const started = descJob.createdAt ? new Date(descJob.createdAt as any).getTime() : Date.now();
        setGenStartTime(started);
        setProgressAnimating(true);
      }
      if (Array.isArray(descJob.progressSteps)) {
        const steps = descJob.progressSteps as string[];
        setProgressSteps(prev => (steps.length > prev.length ? steps : prev));
      }
    }
  }, [descJob, consumedDescJobId, locationId, genStartTime, toast]);

  const generateMutation = useMutation<{ jobId: string }>({
    mutationFn: async () => {
      setProgressSteps([]);
      setProgressAnimating(true);
      setGenStartTime(Date.now());
      setElapsedSeconds(0);
      const res = await apiRequest("POST", `/api/gbp/service-descriptions/${locationId}`, {
        currentServices: allCurrentServices,
        googleRecommendedServices: [],
        credential: credential || undefined,
        keywords: trackedKeywords,
        keywordVariants: {},
        geogridZones,
        businessName,
        primaryCategory,
      });
      return res.json();
    },
    onSuccess: (data) => {
      setConsumedDescJobId(null);
      setDescJobId(data.jobId);
    },
    onError: (err: any) => {
      setGenStartTime(null);
      setProgressAnimating(false);
      toast({ title: "Build Failed", description: err.message || "Could not generate descriptions", variant: "destructive" });
    },
  });

  const isDescRunning = generateMutation.isPending
    || (!!descJob && descJob.status === "processing")
    || (!!descJobId && !descJob);

  useEffect(() => {
    try {
      const savedTitles = localStorage.getItem(`dd_svc_titles_${locationId}`);
      setAdditionalTitles(savedTitles ? JSON.parse(savedTitles) : []);
    } catch { setAdditionalTitles([]); }
    try {
      const savedUnchecked = localStorage.getItem(`dd_svc_unchecked_${locationId}`);
      setUncheckedTitles(savedUnchecked ? new Set(JSON.parse(savedUnchecked)) : new Set());
    } catch { setUncheckedTitles(new Set()); }
    try {
      const saved = localStorage.getItem(`dd_push_done_${locationId}`);
      if (saved) setPushStatus(JSON.parse(saved));
      else setPushStatus({ state: "idle" });
    } catch { setPushStatus({ state: "idle" }); }
    setShowPushConfirm(false);
    try {
      setPushCoachDismissed(localStorage.getItem(`dd_push_coach_dismissed_${locationId}`) === "1");
    } catch { setPushCoachDismissed(false); }
    setProgressSteps([]);
    setProgressAnimating(false);
    setGenStartTime(null);
    setElapsedSeconds(0);
    fillMutation.reset();
    generateMutation.reset();
    setTitlesJobId(null);
    setDescJobId(null);
    setConsumedTitlesJobId(null);
    setConsumedDescJobId(null);
    setTitlesKickedThisLocation(false);
    try {
      const savedExcluded = localStorage.getItem(`dd_svc_excluded_${locationId}`);
      setExcludedIdx(savedExcluded ? new Set(JSON.parse(savedExcluded)) : new Set());
    } catch { setExcludedIdx(new Set()); }
    try {
      const savedEdits = localStorage.getItem(`dd_svc_edits_${locationId}`);
      setEditDrafts(savedEdits ? JSON.parse(savedEdits) : {});
    } catch { setEditDrafts({}); }
    setEditingIdx(null);
    setFilter("all");
    try {
      const cachedDesc = localStorage.getItem(`dd_desc_result_${locationId}`);
      if (cachedDesc) {
        const parsed = JSON.parse(cachedDesc);
        if (parsed && parsed.services?.length > 0) {
          setCachedDescData(parsed);
          setGeneratedForLocationId(locationId);
        } else {
          setCachedDescData(null);
          setGeneratedForLocationId(null);
        }
      } else {
        setCachedDescData(null);
        setGeneratedForLocationId(null);
      }
    } catch {
      setCachedDescData(null);
      setGeneratedForLocationId(null);
    }
  }, [locationId]);

  const descData = generatedForLocationId === locationId ? cachedDescData : null;
  const step2Done = !!descData;
  const pushDone = pushStatus.state === "success";

  const dismissPushCoach = () => {
    setPushCoachDismissed(true);
    try { localStorage.setItem(`dd_push_coach_dismissed_${locationId}`, "1"); } catch {}
  };

  const handleReset = () => {
    try { localStorage.removeItem(pushLatchKey); } catch {}
    try { localStorage.removeItem(`dd_desc_result_${locationId}`); } catch {}
    try { localStorage.removeItem(titlesKey); } catch {}
    try { localStorage.removeItem(uncheckedKey); } catch {}
    setPushStatus({ state: "idle" });
    setGeneratedForLocationId(null);
    setCachedDescData(null);
    setProgressSteps([]);
    setProgressAnimating(false);
    setGenStartTime(null);
    setElapsedSeconds(0);
    generateMutation.reset();
    fillMutation.reset();
    setTitlesJobId(null);
    setDescJobId(null);
    setConsumedTitlesJobId(latestTitlesJob?.id ?? null);
    setConsumedDescJobId(latestDescJob?.id ?? null);
    setAdditionalTitles([]);
    setUncheckedTitles(new Set());
  };

  // Apply per-item curation (excludes + edits) to produce the effective list.
  const effectiveServices = (descData?.services || []).map((s, i) => {
    const draft = editDrafts[i];
    return {
      ...s,
      upgradedName: draft?.name ?? s.upgradedName,
      optimizedDescription: draft?.description ?? s.optimizedDescription,
      _idx: i,
      _excluded: excludedIdx.has(i),
      _edited: !!draft,
    };
  });
  const selectedServices = effectiveServices.filter(s => !s._excluded);
  const coachVisible =
    !pushCoachDismissed && !!location?.gbpLocationId && selectedServices.length > 0;
  // Position the push coach-mark horizontally centered on the viewport but
  // vertically aligned with the "Push to GBP" button. CSS alone can't combine
  // viewport-horizontal-centering with button-relative-vertical alignment, so
  // measure the button and drive a fixed-position popup from its center.
  useEffect(() => {
    if (!coachVisible) {
      setCoachTop(null);
      return;
    }
    const measure = () => {
      const r = pushBtnRef.current?.getBoundingClientRect();
      if (r) setCoachTop(r.top + r.height / 2);
    };
    measure();
    const raf = requestAnimationFrame(measure);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [coachVisible]);
  const expansionCount = effectiveServices.filter(s => (s.landmarkZone || "green") === "red").length;
  const editedCount = Object.keys(editDrafts).length;
  const filteredServices = effectiveServices.filter(s => {
    if (filter === "all") return true;
    if (filter === "expansion") return (s.landmarkZone || "green") === "red";
    if (filter === "edited") return s._edited;
    if (filter === "excluded") return s._excluded;
    return true;
  });

  const handlePush = async () => {
    if (!descData || generatedForLocationId !== locationId) return;
    setShowPushConfirm(false);
    setPushStatus({ state: "pushing" });
    const servicesToPush = selectedServices.map(s => ({
      name: s.upgradedName || s.name,
      description: s.optimizedDescription,
      isProtected: s.isProtected,
      googleServiceTypeId: s.googleServiceTypeId,
    }));
    try {
      const res = await apiRequest("POST", `/api/gbp/push-services/${locationId}`, { services: servicesToPush, mode: "all" });
      const result = await res.json();
      if (result.success) {
        const now = new Date();
        const ts = now.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) + " at " + now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
        const successState = { state: "success" as const, message: `${result.pushedCount} services live on Google Business Profile`, timestamp: ts };
        setPushStatus(successState);
        try { localStorage.setItem(pushLatchKey, JSON.stringify(successState)); } catch {}
        toast({ title: "Services Pushed!", description: `${result.pushedCount} services updated on GBP` });
        onApprove();
      } else {
        setPushStatus({ state: "error", message: result.error });
      }
    } catch (err: any) {
      setPushStatus({ state: "error", message: err.message });
    }
  };

  const summaryMetrics = descData ? (() => {
    const svcs = descData.services;
    const avgCharBefore = svcs.length > 0
      ? Math.round(svcs.reduce((sum, s) => sum + (s.currentDescription?.length || 0), 0) / svcs.length)
      : 0;
    const avgCharAfter = svcs.length > 0
      ? Math.round(svcs.reduce((sum, s) => sum + s.charCount, 0) / svcs.length)
      : 0;
    const keywordHits = svcs.filter(s => s.matchedKeyword && s.optimizedDescription.toLowerCase().includes(s.matchedKeyword.toLowerCase())).length;
    const keywordDensity = svcs.length > 0 ? Math.round((keywordHits / svcs.length) * 100) : 0;
    const zoneCounts = { red: 0, orange: 0, yellow: 0, green: 0 };
    for (const s of svcs) {
      const z = s.landmarkZone || "green";
      if (z in zoneCounts) zoneCounts[z as keyof typeof zoneCounts]++;
    }
    const crossStreetHits = svcs.filter(s => {
      const match = s.assignedLandmark?.match(/\(([^)]+&[^)]+)\)/);
      return match && s.optimizedDescription.includes(match[1]);
    }).length;
    const crossStreetPct = svcs.length > 0 ? Math.round((crossStreetHits / svcs.length) * 100) : 0;
    const serviceNameInDesc = svcs.filter(s => {
      const words = (s.upgradedName || s.name).toLowerCase().split(" ").filter(w => w.length > 3);
      const descLower = s.optimizedDescription.toLowerCase();
      return words.filter(w => descLower.includes(w)).length >= 2;
    }).length;
    const serviceNamePct = svcs.length > 0 ? Math.round((serviceNameInDesc / svcs.length) * 100) : 0;
    const triggerTokenHits = svcs.filter(s => {
      const allTokens = ["24 Hour", "Same Day", "Fast Response", "On-Site in 30 Min", "Available Now", "Immediate Dispatch", "Damage-Free", "Guaranteed", "Licensed & Insured", "Certified Specialists", "No Hidden Fees", "Battery-Safe Transport", "Contracted Service", "Fully Licensed", "Insurance Approved", "DOT Compliant", "Professionally Managed", "Free Tow Included", "Cash Paid on Pickup", "Free Estimate", "No-Cost Removal", "Instant Quote"];
      const nameLower = (s.upgradedName || "").toLowerCase();
      return allTokens.some(t => nameLower.includes(t.toLowerCase()));
    }).length;
    const triggerTokenPct = svcs.length > 0 ? Math.round((triggerTokenHits / svcs.length) * 100) : 0;
    return { avgCharBefore, avgCharAfter, keywordDensity, zoneCounts, crossStreetPct, serviceNamePct, triggerTokenPct };
  })() : null;

  if (isLocked) {
    return (
      <Card className="border-[#1E1E2E] bg-[#12121A] relative overflow-hidden" data-testid="card-step3-locked">
        <div className="absolute inset-0 backdrop-blur-[6px] bg-[#12121A]/60 z-10 flex items-center justify-center">
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E1E2E]/90 border border-[#2A2A3A]">
            <Lock className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground font-medium">Complete Steps 1–2 to unlock</span>
          </div>
        </div>
        <CardContent className="p-5 select-none opacity-40">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#1E1E2E] border border-[#2A2A3A] flex items-center justify-center text-sm font-bold text-muted-foreground">3</div>
            <h3 className="font-semibold text-muted-foreground">Optimize Service Descriptions</h3>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className={`border-[#1E1E2E] bg-[#0d0d0d] transition-all ${
        isCompleted ? "border-emerald-500/20" : isActive ? "ring-1 ring-brand-orange/30 border-brand-orange/20" : ""
      }`}
      data-testid="card-step3-services"
    >
      <CardContent className="p-5 md:p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
            isCompleted || pushDone ? "bg-emerald-500/20" : "bg-brand-orange/20 border border-brand-orange/40"
          }`}>
            {isCompleted || pushDone ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            ) : (
              <span className="text-sm font-bold text-brand-orange">3</span>
            )}
          </div>
          <div>
            <h3 className="text-base font-semibold text-[#f4f4f5]" data-testid="text-step3-title">Optimize Service Descriptions</h3>
            <p className="text-xs text-[#71717a] mt-0.5">Build geo-signal service micro pages that match high-intent search queries</p>
          </div>
          {(step2Done || pushDone) && (
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto text-xs text-[#71717a] hover:text-[#a1a1aa] hover:bg-white/5"
              onClick={handleReset}
              data-testid="button-back-reset"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1" />
              Start Over
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {!descData && (
          <div className="rounded-lg border border-[#1E1E2E] bg-[#161616] p-4" data-testid="step-1-generate">
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                step1Done || pushDone ? "bg-[#3db87a]/20 text-[#3db87a]" : "bg-[#e8722a]/15 text-[#e8722a]"
              }`}>
                {step1Done || pushDone ? <CheckCircle2 className="w-3.5 h-3.5" /> : "1"}
              </div>
              <span className={`text-sm font-semibold ${step1Done || pushDone ? "text-[#3db87a]" : "text-[#f4f4f5]"}`}>Generate Missing Services</span>
            </div>

            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-[#71717a]">{totalSlots} services found · {Math.max(0, 100 - totalSlots)} slots remaining</span>
              <span className="text-xs font-mono text-[#a1a1aa]">{totalSlots}/100</span>
            </div>
            <div className="w-full bg-[#1e1e1e] rounded-full h-2.5 mb-4">
              <div
                className="h-2.5 rounded-full transition-all duration-500"
                style={{
                  width: `${Math.min(100, totalSlots)}%`,
                  backgroundColor: totalSlots >= 100 ? "#3db87a" : "#e8722a",
                }}
                data-testid="progress-slots"
              />
            </div>

            {(step1Done || pushDone) ? (
              <div className="flex items-center gap-2 text-sm" style={{ color: "#3db87a" }}>
                <CheckCircle2 className="w-4 h-4" />
                <span>✓ {totalSlots} services ready</span>
              </div>
            ) : totalSlots >= MAX_GOOGLE_SERVICES ? (
              <div className="flex items-center gap-2 text-sm" style={{ color: "#3db87a" }}>
                <CheckCircle2 className="w-4 h-4" />
                <span>✓ {totalSlots} services ready (max reached)</span>
              </div>
            ) : (
              <button
                className={`w-full h-11 rounded-lg text-sm font-semibold text-white border-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${isActive && !isTitlesRunning ? "glow-pulse" : ""}`}
                style={{ backgroundColor: "#e8722a", fontFamily: "inherit" }}
                onClick={() => fillMutation.mutate()}
                disabled={isTitlesRunning}
                data-testid="button-generate-services"
              >
                {isTitlesRunning ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating services…
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Generate {Math.max(0, MAX_GOOGLE_SERVICES - totalSlots)} Missing Services
                  </span>
                )}
              </button>
            )}

            {additionalTitles.length > 0 && !step2Done && (
              <div className="mt-3 max-h-48 overflow-y-auto space-y-1 border-t border-[#1e1e1e] pt-3">
                {additionalTitles.map((title, i) => (
                  <label key={i} className="flex items-center gap-2 text-xs text-zinc-300 hover:text-zinc-100 cursor-pointer py-0.5">
                    <input
                      type="checkbox"
                      checked={!uncheckedTitles.has(title)}
                      onChange={() => setUncheckedTitles(prev => {
                        const next = new Set(prev);
                        if (next.has(title)) next.delete(title); else next.add(title);
                        return next;
                      })}
                      className="w-3.5 h-3.5"
                      style={{ accentColor: "#e8722a" }}
                      data-testid={`checkbox-title-${i}`}
                    />
                    {title}
                  </label>
                ))}
                <p className="text-[11px] text-[#52525b] mt-2">
                  {additionalTitles.length} new services generated — uncheck any you want to remove
                </p>
              </div>
            )}
          </div>
          )}

          {!descData && (
          <div className={`rounded-lg border p-4 transition-all ${
            step1Done && !step2Done && !pushDone ? "border-[#1E1E2E] bg-[#161616]" :
            step2Done || pushDone ? "border-[#3db87a]/10 bg-[#3db87a]/[0.03]" :
            "border-[#1E1E2E]/50 bg-[#161616]/40"
          }`} data-testid="step-2-build">
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                step2Done || pushDone ? "bg-[#3db87a]/20 text-[#3db87a]" :
                step1Done ? "bg-[#e8722a]/15 text-[#e8722a]" :
                "bg-[#1e1e1e] text-[#52525b]"
              }`}>
                {step2Done || pushDone ? <CheckCircle2 className="w-3.5 h-3.5" /> : "2"}
              </div>
              <span className={`text-sm font-semibold ${
                step2Done || pushDone ? "text-[#3db87a]" :
                step1Done ? "text-[#f4f4f5]" :
                "text-[#52525b]"
              }`}>Upgrade Names & Build Descriptions</span>
            </div>

            {isDescRunning && (
              <div className="space-y-2 mb-3 pl-8">
                {progressSteps.map((step, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs" style={{ color: "#3db87a" }}>
                    <CheckCircle2 className="w-3 h-3 shrink-0" />
                    <span style={{ fontFamily: "monospace" }}>✓ {step}</span>
                  </div>
                ))}
                {progressAnimating && progressSteps.length < 6 && (
                  <div className="flex items-center gap-2 text-xs text-[#e8722a]">
                    <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                    <span style={{ fontFamily: "monospace" }}>Processing...</span>
                  </div>
                )}
                <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: "#1e1e1e", borderRadius: "8px" }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-[#a1a1aa]">
                      Optimizing {totalSlots} services with AI — this typically takes 2–4 minutes
                    </span>
                    <span className="text-xs font-mono" style={{ color: "#e8722a" }}>
                      {Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="w-full bg-[#0d0d0d] rounded-full h-1.5 overflow-hidden">
                    <div
                      className="h-1.5 rounded-full transition-all duration-1000"
                      style={{
                        width: `${Math.min(95, (elapsedSeconds / 240) * 100)}%`,
                        backgroundColor: "#e8722a",
                      }}
                    />
                  </div>
                  <p className="text-[11px] mt-2" style={{ color: "#52525b" }}>
                    You can navigate to other pages while this runs — your results will be here when you come back.
                  </p>
                </div>
              </div>
            )}

            {(step2Done || pushDone) ? (
              <div className="flex items-center gap-2 text-sm" style={{ color: "#3db87a" }}>
                <CheckCircle2 className="w-4 h-4" />
                <span>Descriptions built</span>
              </div>
            ) : (
              <button
                className={`w-full h-11 rounded-lg text-sm font-semibold text-white border-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
                style={{
                  backgroundColor: step1Done ? "#e8722a" : "#1e1e1e",
                  color: step1Done ? "white" : "#52525b",
                  fontFamily: "inherit",
                }}
                onClick={() => step1Done && generateMutation.mutate()}
                disabled={!step1Done || isDescRunning}
                data-testid="button-build-descriptions"
              >
                {isDescRunning ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Upgrading names & building descriptions…
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Wrench className="w-4 h-4" />
                    Upgrade Names & Build Descriptions
                  </span>
                )}
              </button>
            )}
          </div>
          )}

          {descData && (
            <TooltipProvider delayDuration={150}>
              <div className="space-y-3 mt-2" data-testid="service-list">
                {/* Outcome summary bar — replaces the dual completion banners */}
                {pushStatus.state === "idle" && (
                  <div className="rounded-lg border border-emerald-500/15 bg-emerald-500/[0.04] p-3 flex items-center justify-between flex-wrap gap-3" data-testid="bar-outcome">
                    <div className="flex items-center gap-2 min-w-0">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span className="text-sm text-emerald-300">
                        {selectedServices.length === effectiveServices.length
                          ? `${effectiveServices.length} services generated and optimized · Ready to push`
                          : `${selectedServices.length} of ${effectiveServices.length} services selected · Ready to push`}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                      <div className="relative">
                      <button
                        onClick={() => {
                          dismissPushCoach();
                          setShowPushConfirm(true);
                          // The actual confirm dialog renders much further down
                          // the page (after Steps 1+2). Wait a tick for it to
                          // mount, then scroll it into view so the user can
                          // actually click the working "Yes, Push to GBP" button.
                          setTimeout(() => {
                            const el = document.querySelector('[data-testid="push-confirm-dialog"]');
                            if (el) {
                              el.scrollIntoView({ behavior: "smooth", block: "center" });
                            }
                          }, 50);
                        }}
                        disabled={!location?.gbpLocationId || selectedServices.length === 0}
                        className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg text-base font-bold bg-brand-orange text-white hover:bg-brand-orange/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-brand-orange/20 ${isActive && location?.gbpLocationId && selectedServices.length > 0 ? "glow-pulse" : ""}`}
                        ref={pushBtnRef}
                        data-testid="button-push-to-gbp-top"
                      >
                        Push to GBP
                        <ArrowRight className="w-5 h-5" />
                      </button>
                      {coachVisible && coachTop !== null && (
                        <div
                          className="fixed left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none"
                          style={{ top: coachTop }}
                          data-testid="coach-push-to-gbp"
                        >
                          <div className="animate-bounce pointer-events-auto">
                            <div className="relative rounded-xl bg-brand-orange text-white shadow-2xl px-5 py-3.5 text-sm font-semibold flex items-center gap-2.5 whitespace-nowrap">
                              <span className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-3.5 h-3.5 rotate-45 bg-brand-orange" />
                              <span>Click "Push to GBP" to publish your services</span>
                              <ArrowRight className="w-5 h-5 shrink-0" />
                              <button
                                onClick={dismissPushCoach}
                                className="shrink-0 -mr-1 rounded p-0.5 hover:bg-white/20 transition-colors"
                                data-testid="button-dismiss-push-coach"
                                aria-label="Dismiss"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      </div>
                      <button
                        data-testid="button-copy-services"
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium text-[#a1a1aa] hover:text-white hover:bg-white/5 transition-colors"
                        onClick={() => {
                          const text = selectedServices.map((svc, i) =>
                            `${i + 1}. ${svc.upgradedName || svc.name}\n${svc.optimizedDescription || svc.currentDescription || "(no description)"}`
                          ).join("\n\n");
                          navigator.clipboard.writeText(text).then(() => {
                            setCopiedServices(true);
                            setTimeout(() => setCopiedServices(false), 2000);
                            toast({ title: "Copied!", description: `${selectedServices.length} services copied to clipboard` });
                          });
                        }}
                      >
                        {copiedServices ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                        {copiedServices ? "Copied" : "Copy All"}
                      </button>
                      <button
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium text-[#a1a1aa] hover:text-white hover:bg-white/5 transition-colors"
                        data-testid="button-export-csv"
                        onClick={() => {
                          const escape = (v: string) => `"${(v || "").replace(/"/g, '""')}"`;
                          const rows = [
                            ["Name", "Description", "Landmark", "Keyword", "Chars"],
                            ...selectedServices.map(s => [s.upgradedName || s.name, s.optimizedDescription, s.assignedLandmark || "", s.matchedKeyword || "", String(s.charCount)]),
                          ];
                          const csv = rows.map(r => r.map(escape).join(",")).join("\n");
                          const blob = new Blob([csv], { type: "text/csv" });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `services-${businessName.toLowerCase().replace(/\s+/g, "-") || "export"}.csv`;
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                      >
                        <FileText className="w-3.5 h-3.5" />
                        Export CSV
                      </button>
                    </div>
                  </div>
                )}

                {/* Filter / sort bar */}
                <div className="flex items-center gap-1.5 flex-wrap pt-1" data-testid="bar-filter">
                  {([
                    { key: "all", label: "All", count: effectiveServices.length },
                    { key: "expansion", label: "Expansion Targets", count: expansionCount },
                    { key: "edited", label: "Edited", count: editedCount },
                    { key: "excluded", label: "Excluded", count: excludedIdx.size },
                  ] as const).map(f => (
                    <button
                      key={f.key}
                      onClick={() => setFilter(f.key as any)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        filter === f.key
                          ? "bg-brand-orange/15 border-brand-orange/30 text-brand-orange"
                          : "bg-transparent border-[#2A2A3A] text-[#a1a1aa] hover:text-white hover:border-[#3a3a4a]"
                      }`}
                      data-testid={`filter-${f.key}`}
                    >
                      {f.label} ({f.count})
                    </button>
                  ))}
                </div>

                {/* Service list — denser, sans-serif body, conditional before/after */}
                <div className="rounded-lg border border-[#1E1E2E] bg-[#0d0d0d] divide-y divide-[#1E1E2E] overflow-hidden">
                  {filteredServices.length === 0 && (
                    <div className="p-6 text-center text-sm text-muted-foreground">
                      No services match this filter.
                    </div>
                  )}
                  {filteredServices.map((svc) => {
                    const i = svc._idx;
                    const crossMatch = svc.assignedLandmark?.match(/^(.+?)\s*\(([^)]+&[^)]+)\)$/);
                    const landmarkName = crossMatch ? crossMatch[1] : svc.assignedLandmark;
                    const crossStreet = crossMatch ? crossMatch[2] : "";
                    const zone = svc.landmarkZone || "green";
                    const hasBefore = !!(svc.currentDescription && svc.currentDescription.trim());
                    const isEditing = editingIdx === i;
                    const draft = editDrafts[i];
                    return (
                      <div
                        key={i}
                        className={`px-4 py-3 transition-colors ${svc._excluded ? "opacity-40" : "hover:bg-white/[0.015]"}`}
                        data-testid={`service-card-${i}`}
                      >
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <span className="text-[11px] font-mono text-[#52525b] shrink-0 w-6">#{i + 1}</span>
                            {isEditing ? (
                              <Input
                                value={draft?.name ?? svc.upgradedName ?? svc.name}
                                onChange={e => setEditDrafts(prev => ({ ...prev, [i]: { name: e.target.value, description: prev[i]?.description ?? svc.optimizedDescription } }))}
                                className="h-7 text-sm font-semibold bg-[#0a0a0a] border-[#2A2A3A]"
                                data-testid={`input-name-${i}`}
                              />
                            ) : (
                              <span className="text-sm font-semibold text-white truncate">
                                {svc.upgradedName || svc.name}
                                {!hasBefore && (
                                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-400 align-middle">New</span>
                                )}
                                {svc._edited && (
                                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-brand-blue/15 text-brand-blue align-middle">Edited</span>
                                )}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {/* Zone badge with tooltip */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full cursor-help"
                                  style={{
                                    backgroundColor: `${ZONE_COLORS[zone] || ZONE_COLORS.green}15`,
                                    color: ZONE_COLORS[zone] || ZONE_COLORS.green,
                                    border: `1px solid ${ZONE_COLORS[zone] || ZONE_COLORS.green}30`,
                                  }}
                                >
                                  {ZONE_LABELS[zone]?.icon} {ZONE_LABELS[zone]?.label || zone}
                                  {zone === "red" && <Info className="w-2.5 h-2.5 opacity-60" />}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="max-w-xs text-xs">
                                {zone === "red"
                                  ? "Expansion target — generated to capture adjacent keyword searches you're not currently ranking for."
                                  : zone === "orange"
                                    ? "Climbing — landmark sits in a zone where you're ranking 8–13."
                                    : zone === "yellow"
                                      ? "Top 10 — landmark sits in a zone where you're already in the top 10."
                                      : "Top 3 locked — landmark sits in a zone where you're already dominating."}
                              </TooltipContent>
                            </Tooltip>
                            {/* Per-item actions */}
                            <div className="flex items-center">
                              {isEditing ? (
                                <button
                                  onClick={() => setEditingIdx(null)}
                                  className="p-1.5 rounded hover:bg-emerald-500/10 text-emerald-400"
                                  title="Save"
                                  data-testid={`button-save-${i}`}
                                >
                                  <Save className="w-3.5 h-3.5" />
                                </button>
                              ) : (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => { setEditingIdx(i); setExcludedIdx(prev => { const n = new Set(prev); n.delete(i); return n; }); }}
                                      className="p-1.5 rounded hover:bg-white/5 text-[#71717a] hover:text-white"
                                      data-testid={`button-edit-${i}`}
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>Edit</TooltipContent>
                                </Tooltip>
                              )}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => {
                                      setExcludedIdx(prev => {
                                        const n = new Set(prev);
                                        if (n.has(i)) n.delete(i); else n.add(i);
                                        return n;
                                      });
                                      if (editingIdx === i) setEditingIdx(null);
                                    }}
                                    className={`p-1.5 rounded hover:bg-white/5 ${svc._excluded ? "text-emerald-400" : "text-[#71717a] hover:text-red-400"}`}
                                    data-testid={`button-toggle-exclude-${i}`}
                                  >
                                    {svc._excluded ? <RefreshCw className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>{svc._excluded ? "Restore" : "Remove from push"}</TooltipContent>
                              </Tooltip>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-x-4 gap-y-1 text-[11px] text-[#a1a1aa] mb-2 pl-8 flex-wrap">
                          <div><span className="text-[#52525b]">Landmark: </span><span>{landmarkName} {crossStreet ? `(${crossStreet})` : ""}</span></div>
                          <div><span className="text-[#52525b]">Keyword: </span><span className="text-amber-400">{svc.matchedKeyword || "—"}</span></div>
                          <div><span className="text-[#52525b]">Chars: </span><span className="text-emerald-400 tabular-nums">{svc.charCount}/300</span></div>
                        </div>

                        {hasBefore && !isEditing && (
                          <div className="pl-8 mb-2">
                            <p className="text-[10px] uppercase tracking-widest text-[#52525b] mb-0.5 font-medium">Before</p>
                            <p className="text-xs text-[#666] italic leading-[1.5]">{svc.currentDescription}</p>
                          </div>
                        )}

                        <div className="pl-8">
                          {hasBefore && !isEditing && (
                            <p className="text-[10px] uppercase tracking-widest text-emerald-400/60 mb-0.5 font-medium">After</p>
                          )}
                          {isEditing ? (
                            <Textarea
                              value={draft?.description ?? svc.optimizedDescription}
                              onChange={e => setEditDrafts(prev => ({ ...prev, [i]: { name: prev[i]?.name ?? svc.upgradedName ?? svc.name, description: e.target.value } }))}
                              className="min-h-[80px] text-[13px] leading-[1.5] bg-[#0a0a0a] border-[#2A2A3A] resize-none"
                              data-testid={`input-description-${i}`}
                            />
                          ) : (
                            <p className="text-[13px] leading-[1.5] text-zinc-200">
                              {highlightKeywordsInText(svc.optimizedDescription, trackedKeywords)}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </TooltipProvider>
          )}

          {summaryMetrics && (
            <div className="rounded-lg border p-4 md:p-5 space-y-3" style={{
              borderColor: "rgba(255,255,255,0.06)",
              backgroundColor: "#161616",
              borderRadius: "8px",
            }} data-testid="summary-card">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-4 h-4" style={{ color: "#e8722a" }} />
                <span className="text-xs font-bold tracking-wider uppercase" style={{ color: "#e8722a" }}>Optimization Summary</span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-md p-3" style={{ backgroundColor: "#1e1e1e", borderRadius: "8px" }}>
                  <p className="text-[10px] uppercase tracking-wider text-[#71717a] mb-1">Avg Character Utilization</p>
                  <p className="text-sm font-semibold">
                    <span className="text-[#71717a]">{summaryMetrics.avgCharBefore}</span>
                    <span className="text-[#52525b] mx-1.5">→</span>
                    <span style={{ color: "#3db87a" }}>{summaryMetrics.avgCharAfter}</span>
                    <span className="ml-1.5" style={{ color: "#3db87a" }}>✓</span>
                  </p>
                </div>

                <div className="rounded-md p-3" style={{ backgroundColor: "#1e1e1e", borderRadius: "8px" }}>
                  <p className="text-[10px] uppercase tracking-wider text-[#71717a] mb-1">Keyword Signal Density</p>
                  <p className="text-sm font-semibold">
                    <span className="text-[#71717a]">0%</span>
                    <span className="text-[#52525b] mx-1.5">→</span>
                    <span style={{ color: "#3db87a" }}>
                      {summaryMetrics.keywordDensity}%
                    </span>
                    <span className="ml-1.5" style={{ color: "#3db87a" }}>✓</span>
                  </p>
                </div>

                <div className="rounded-md p-3 col-span-2" style={{ backgroundColor: "#1e1e1e", borderRadius: "8px" }}>
                  <p className="text-[10px] uppercase tracking-wider text-[#71717a] mb-2">Territory Targeting</p>
                  <div className="flex items-center gap-4 flex-wrap">
                    {(["green", "yellow", "orange", "red"] as const).map(z => (
                      <div key={z} className="flex items-center gap-1.5 text-xs">
                        <span>{ZONE_LABELS[z].icon}</span>
                        <span className="text-[#a1a1aa]">{ZONE_LABELS[z].label}</span>
                        <span className="font-mono font-semibold" style={{ color: ZONE_COLORS[z] }}>{summaryMetrics.zoneCounts[z]}</span>
                        <span className="text-[#52525b]">placements</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-md p-3" style={{ backgroundColor: "#1e1e1e", borderRadius: "8px" }}>
                  <p className="text-[10px] uppercase tracking-wider text-[#71717a] mb-1">Cross Street Coverage</p>
                  <p className="text-sm font-semibold">
                    <span style={{ color: "#3db87a" }}>
                      {summaryMetrics.crossStreetPct}%
                    </span>
                    <span className="ml-1.5" style={{ color: "#3db87a" }}>✓</span>
                  </p>
                </div>

                <div className="rounded-md p-3" style={{ backgroundColor: "#1e1e1e", borderRadius: "8px" }}>
                  <p className="text-[10px] uppercase tracking-wider text-[#71717a] mb-1">Service Name in Description</p>
                  <p className="text-sm font-semibold">
                    <span style={{ color: "#3db87a" }}>
                      {summaryMetrics.serviceNamePct}%
                    </span>
                    <span className="ml-1.5" style={{ color: "#3db87a" }}>✓</span>
                  </p>
                </div>

                <div className="rounded-md p-3 col-span-2" style={{ backgroundColor: "#1e1e1e", borderRadius: "8px" }}>
                  <p className="text-[10px] uppercase tracking-wider text-[#71717a] mb-1">Trigger Token Coverage</p>
                  <p className="text-sm font-semibold">
                    <span style={{ color: "#3db87a" }}>
                      {summaryMetrics.triggerTokenPct}%
                    </span>
                    <span className="ml-1.5" style={{ color: "#3db87a" }}>✓</span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {step2Done && pushStatus.state === "idle" && (
            <div className="space-y-3" data-testid="step-push">
              {showPushConfirm && (
                <div className="p-4 rounded-lg border space-y-3" style={{
                  backgroundColor: "#161616",
                  borderColor: "#e8722a30",
                  borderRadius: "8px",
                }} data-testid="push-confirm-dialog">
                  <p className="text-sm text-zinc-200">
                    You are about to update <strong>{selectedServices.length}</strong> services on <strong>{businessName}</strong>'s GBP listing. This replaces all existing freeform services. Continue?
                  </p>
                  <div className="flex gap-2">
                    <button
                      className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-white border-none cursor-pointer"
                      style={{ backgroundColor: "#e8722a", borderRadius: "8px", fontFamily: "inherit" }}
                      onClick={handlePush}
                      data-testid="button-confirm-push"
                    >
                      Yes, Push to GBP
                    </button>
                    <button
                      className="flex-1 py-2.5 rounded-lg text-sm font-semibold border cursor-pointer"
                      style={{ backgroundColor: "transparent", borderColor: "#2A2A3A", color: "#a1a1aa", borderRadius: "8px", fontFamily: "inherit" }}
                      onClick={() => setShowPushConfirm(false)}
                      data-testid="button-cancel-push"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {pushStatus.state === "pushing" && (
            <div className="p-4 rounded-lg flex items-center justify-center gap-3" style={{
              backgroundColor: "#e8722a08",
              border: "1px solid #e8722a20",
              borderRadius: "8px",
            }}>
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#e8722a" }} />
              <p className="text-sm font-medium" style={{ color: "#e8722a" }}>Pushing to Google Business Profile…</p>
            </div>
          )}

          {pushStatus.state === "error" && (
            <div className="space-y-3">
              <div className="p-3 rounded-lg flex items-center gap-2" style={{
                backgroundColor: "#e8722a08",
                border: "1px solid #e8722a20",
                borderRadius: "8px",
              }}>
                <RefreshCw className="w-5 h-5" style={{ color: "#e8722a" }} />
                <p className="text-sm font-medium" style={{ color: "#e8722a" }}>Push didn't go through — ready to retry.</p>
              </div>
              <button
                className="text-sm px-4 py-2 rounded-lg border cursor-pointer"
                style={{ borderColor: "#e8722a30", color: "#e8722a", backgroundColor: "transparent", fontFamily: "inherit", borderRadius: "8px" }}
                onClick={() => setPushStatus({ state: "idle" })}
                data-testid="button-retry-push"
              >
                ↻ Retry Push
              </button>
            </div>
          )}

          {pushStatus.state === "success" && (
            <div className="p-4 rounded-lg space-y-2" style={{
              backgroundColor: "#3db87a08",
              border: "1px solid #3db87a20",
              borderRadius: "8px",
            }}>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" style={{ color: "#3db87a" }} />
                <p className="text-sm font-semibold" style={{ color: "#3db87a" }}>✓ All {descData?.stats.totalServices || ""} services pushed successfully</p>
              </div>
              {pushStatus.timestamp && (
                <p className="text-xs text-[#71717a] pl-7">Pushed {pushStatus.timestamp}</p>
              )}
              <p className="text-xs text-[#a1a1aa] pl-7">{pushStatus.message}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}



export default function GbpOptimizationPage() {
  const { selectedLocationId } = useLocationStore();
  const { toast } = useToast();

  const { data: locations } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
  });

  const locationId = selectedLocationId || locations?.[0]?.id;

  const { data: tasks = [], isLoading } = useQuery<AssistantTask[]>({
    queryKey: ["/api/assistant/tasks", locationId],
    enabled: !!locationId,
  });

  const { data: profileData } = useQuery<ProfileCompleteness>({
    queryKey: ["/api/gbp/profile-completeness", locationId],
    enabled: !!locationId,
  });

  // Seed Aria's optimization tasks on first visit, then refetch the tasks
  // query so the page actually shows them. Without the invalidate the tasks
  // list stays at the empty [] from the initial fetch and the user sees
  // "No Optimizations Yet" forever even after a scan has run.
  useEffect(() => {
    if (!locationId) return;
    let cancelled = false;
    apiRequest("POST", `/api/assistant/seed/${locationId}`)
      .then(() => {
        if (cancelled) return;
        queryClient.invalidateQueries({ queryKey: ["/api/assistant/tasks", locationId] });
      })
      .catch((err) => console.warn("[seed:assistant]", err));
    return () => { cancelled = true; };
  }, [locationId]);

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      await apiRequest("PATCH", `/api/assistant/tasks/${id}/status`, { status });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/assistant/tasks", locationId] });
      queryClient.invalidateQueries({ queryKey: ["/api/locations", locationId, "rtd-progress"] });
      queryClient.invalidateQueries({ queryKey: ["/api/assistant/stats", locationId] });
      queryClient.invalidateQueries({ queryKey: ["/api/token-events"] });
      if (variables.status === "approved") {
        toast({ title: "Optimization applied! +10 tokens earned" });
      } else {
        toast({ title: "Step skipped" });
      }
    },
  });

  const categoryTask = tasks.find(t => t.taskType === "category");
  const descriptionTask = tasks.find(t => t.taskType === "description");
  const serviceTask = tasks.find(t => t.taskType === "service");

  const isDone = (t?: AssistantTask) => t && (t.status === "approved" || t.status === "discarded" || t.status === "auto_approved");
  const step1Done = isDone(categoryTask);
  const step2Done = isDone(descriptionTask);
  const step3Done = isDone(serviceTask);

  const completedCount = (step1Done ? 1 : 0) + (step2Done ? 1 : 0) + (step3Done ? 1 : 0);
  const totalSteps = (categoryTask ? 1 : 0) + (descriptionTask ? 1 : 0) + (serviceTask ? 1 : 0);

  const allDone = totalSteps > 0 && completedCount === totalSteps;

  return (
    <div className="p-6 pb-24 space-y-6 overflow-y-auto h-full">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-gbp-opt-title">GBP Optimization</h1>
          <p className="text-sm text-muted-foreground mt-1">Aria's AI-powered recommendations to dominate local search</p>
        </div>
        <RtdOptOut
          locationId={locationId}
          taskIds={["category", "description", "services", "service_desc"]}
          stageName="GBP Optimization"
          warningTitle="Skip GBP Optimization for now?"
          warningBody={
            <p>
              Category, description, and services are the single biggest ranking levers on your
              Google Business Profile. Skipping these usually means lower visibility for the
              searches that matter most. We strongly recommend completing them.
            </p>
          }
        />
      </div>
      <RtdOptOutBanner
        locationId={locationId}
        taskIds={["category", "description", "services", "service_desc"]}
        stageName="GBP Optimization"
      />

      {isLoading ? (
        <div className="space-y-3">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : (
        <div className="space-y-4">
          {totalSteps > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {completedCount} of {totalSteps} {totalSteps === 1 ? "step" : "steps"} complete
                </p>
                {completedCount > 0 && (
                  <p className="text-xs font-medium text-brand-orange tabular-nums" data-testid="text-progress-percent">
                    {Math.round((completedCount / totalSteps) * 100)}%
                  </p>
                )}
              </div>
              <div className="w-full bg-[#1E1E2E] rounded-full h-1.5 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-[400ms] ease-out ${
                    completedCount > 0 ? "bg-gradient-to-r from-brand-orange to-amber-400" : "bg-muted-foreground/20"
                  }`}
                  style={{ width: `${(completedCount / totalSteps) * 100}%` }}
                  data-testid="progress-optimizations"
                />
              </div>
            </div>
          )}

          {!categoryTask && !serviceTask && !descriptionTask ? (
            <Card className="border-border/50">
              <CardContent className="flex flex-col items-center py-12 text-center">
                <Sparkles className="w-12 h-12 text-brand-orange mb-3" />
                <h3 className="text-lg font-semibold">No Optimizations Yet</h3>
                <p className="text-sm text-muted-foreground mt-2">Run a GeoGrid scan first, then Aria will generate optimization steps.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {categoryTask && (
                <Step1CategoryCard
                  task={categoryTask}
                  isActive={!step1Done}
                  isCompleted={!!step1Done}
                  isLocked={false}
                  locationId={locationId!}
                  profileData={profileData}
                  location={locations?.find(l => l.id === locationId)}
                  onApprove={() => statusMutation.mutate({ id: categoryTask.id, status: "approved" })}
                  onDiscard={() => statusMutation.mutate({ id: categoryTask.id, status: "discarded" })}
                />
              )}

              {descriptionTask && (
                <Step2DescriptionCard
                  task={descriptionTask}
                  isActive={!!step1Done && !step2Done}
                  isCompleted={!!step2Done}
                  isLocked={!step1Done}
                  locationId={locationId!}
                  onApprove={() => statusMutation.mutate({ id: descriptionTask.id, status: "approved" })}
                />
              )}

              {serviceTask && (
                <Step3ServicesCard
                  task={serviceTask}
                  isActive={!!step1Done && !!step2Done && !step3Done}
                  isCompleted={!!step3Done}
                  isLocked={!step1Done || !step2Done}
                  locationId={locationId!}
                  profileData={profileData}
                  location={locations?.find(l => l.id === locationId)}
                  onApprove={() => statusMutation.mutate({ id: serviceTask.id, status: "approved" })}
                  onDiscard={() => statusMutation.mutate({ id: serviceTask.id, status: "discarded" })}
                />
              )}

            </div>
          )}

        </div>
      )}
    </div>
  );
}